import React, { useContext, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getRepDistribution } from "../../API";

function distColor(pct) {
  if (pct >= 70) return "success";
  if (pct >= 40) return "warning";
  return "danger";
}

function DistBadge({ pct }) {
  const c = distColor(pct);
  return (
    <span className={`badge bg-${c}${c === "warning" ? " text-dark" : ""}`}>
      {pct.toFixed(1)}%
    </span>
  );
}

function DistBar({ pct }) {
  const c = distColor(pct);
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 140 }}>
      <DistBadge pct={pct} />
      <div className="progress flex-grow-1" style={{ height: 7 }}>
        <div className={`progress-bar bg-${c}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

// Rank band used for row highlight: top third green, middle yellow, bottom red
function repRankColor(index, total) {
  if (total <= 1) return "";
  const third = total / 3;
  if (index < third)         return "table-success";
  if (index < third * 2)     return "table-warning";
  return "table-danger";
}

export default function DistributionDropReport() {
  const [{ fromDate, toDate, representatives, manufacturers }] = useContext(ReportParamsContext);

  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [expandedReps, setExpandedReps] = useState(new Set());
  const [expandedMfrs, setExpandedMfrs] = useState(new Map()); // repId -> Set<mfrId>

  const handleGenerate = async () => {
    if (!fromDate || !toDate) { setError("Please select at least one month in the parameters bar."); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await getRepDistribution({
        fromDate,
        toDate,
        representativeIds: representatives.map((r) => r.id),
        manufacturerIds: manufacturers.map((m) => m.id),
      });
      setData(result);
      setExpandedReps(new Set());
      setExpandedMfrs(new Map());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleRep = (repId) => {
    setExpandedReps((prev) => {
      const next = new Set(prev);
      next.has(repId) ? next.delete(repId) : next.add(repId);
      return next;
    });
  };

  const toggleMfr = (repId, mfrId) => {
    setExpandedMfrs((prev) => {
      const next = new Map(prev);
      const repSet = new Set(next.get(repId) ?? []);
      repSet.has(mfrId) ? repSet.delete(mfrId) : repSet.add(mfrId);
      next.set(repId, repSet);
      return next;
    });
  };

  const isMfrExpanded = (repId, mfrId) => expandedMfrs.get(repId)?.has(mfrId) ?? false;

  const avgOverall = data?.reps.length
    ? data.reps.reduce((s, r) => s + r.overallAvgPct, 0) / data.reps.length
    : 0;

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        <div className="card shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted small">
                Use the date range above to set the period, then generate.
              </span>
              <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
                {loading ? "Loading…" : "Generate Report"}
              </button>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {data && (
          <>
            {/* Summary cards */}
            <div className="row g-3 mb-3">
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold">{data.reps.length}</div>
                    <div className="text-muted small">Reps active</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold">
                      <DistBadge pct={avgOverall} />
                    </div>
                    <div className="text-muted small">Avg distribution</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold text-success">
                      {data.reps[0]?.repName ?? "—"}
                    </div>
                    <div className="text-muted small">
                      Top performer ({data.reps[0]?.overallAvgPct.toFixed(1) ?? 0}%)
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-4 fw-bold text-danger">
                      {data.reps.at(-1)?.repName ?? "—"}
                    </div>
                    <div className="text-muted small">
                      Needs attention ({data.reps.at(-1)?.overallAvgPct.toFixed(1) ?? 0}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="d-flex align-items-center gap-3 mb-3 small text-muted">
              <span>Distribution:</span>
              <span><span className="badge bg-success me-1">■</span>≥ 70%</span>
              <span><span className="badge bg-warning text-dark me-1">■</span>40–69%</span>
              <span><span className="badge bg-danger me-1">■</span>&lt; 40%</span>
              <span className="ms-2">Row color shows rep rank (top / middle / bottom third)</span>
            </div>

            {data.reps.length === 0 ? (
              <div className="alert alert-info">
                No visits found for this period. Make sure visits were recorded.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: 24 }} />
                      <th>Representative</th>
                      <th style={{ minWidth: 180 }}>Overall Avg Distribution</th>
                      <th className="text-end" style={{ whiteSpace: "nowrap" }}>Stores Visited</th>
                      <th className="text-end">Manufacturers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reps.map((rep, repIdx) => (
                      <React.Fragment key={rep.repId}>

                        {/* Rep row */}
                        <tr
                          className={repRankColor(repIdx, data.reps.length)}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleRep(rep.repId)}
                        >
                          <td className="text-center fw-bold" style={{ userSelect: "none" }}>
                            {expandedReps.has(rep.repId) ? "▾" : "▸"}
                          </td>
                          <td className="fw-semibold">{rep.repName || "(Unknown)"}</td>
                          <td><DistBar pct={rep.overallAvgPct} /></td>
                          <td className="text-end">{rep.totalStores}</td>
                          <td className="text-end">{rep.manufacturers.length}</td>
                        </tr>

                        {/* Manufacturer rows */}
                        {expandedReps.has(rep.repId) && rep.manufacturers.map((mfr) => (
                          <React.Fragment key={mfr.manufacturerId}>

                            <tr
                              className="table-light"
                              style={{ cursor: "pointer", fontSize: "0.82rem" }}
                              onClick={() => toggleMfr(rep.repId, mfr.manufacturerId)}
                            >
                              <td />
                              <td className="ps-4 text-muted" style={{ userSelect: "none" }}>
                                <span className="me-2">
                                  {isMfrExpanded(rep.repId, mfr.manufacturerId) ? "▾" : "▸"}
                                </span>
                                <span className="fw-medium text-dark">{mfr.manufacturerName}</span>
                                <span className="ms-2 text-muted" style={{ fontSize: "0.75rem" }}>
                                  ({mfr.products.length} product{mfr.products.length !== 1 ? "s" : ""})
                                </span>
                              </td>
                              <td><DistBar pct={mfr.avgPct} /></td>
                              <td />
                              <td />
                            </tr>

                            {/* Product rows */}
                            {isMfrExpanded(rep.repId, mfr.manufacturerId) && mfr.products.map((p) => (
                              <tr key={p.productId} style={{ fontSize: "0.78rem", background: "#f8f9fa" }}>
                                <td />
                                <td className="ps-5">
                                  <span className="text-muted me-2">{p.upcCode}</span>
                                  {p.description}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 140 }}>
                                    <DistBadge pct={p.pct} />
                                    <div className="progress flex-grow-1" style={{ height: 5 }}>
                                      <div
                                        className={`progress-bar bg-${distColor(p.pct)}`}
                                        style={{ width: `${Math.min(p.pct, 100)}%` }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td className="text-end text-muted">
                                  {p.yesCount} / {p.totalStores}
                                </td>
                                <td />
                              </tr>
                            ))}

                          </React.Fragment>
                        ))}

                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
